/**
 * Scoped multi-app client factory.
 *
 * `createClient()` returns an isolated, READ-ONLY client bound to one Bounded
 * app, independent of the module-global `init()` client. It exists so a single
 * frontend can hold its normal authenticated client for its own app AND a
 * read-only client for a SECOND Bounded app (e.g. a shared steward/runtime app),
 * with live subscriptions on both and zero interference between them.
 *
 * Each instance carries its OWN fully-resolved config (endpoints + appId) and
 * routes through the SAME shared transport/subscription implementation as the
 * global path - it never forks that code. Isolation comes from threading the
 * instance config (and a read-only marker) through the existing per-request /
 * per-subscription override channel, plus the websocket layer already keying
 * connections by appId.
 *
 * ## v1 scope: read-only, unauthenticated
 *
 * A scoped instance is UNAUTHENTICATED. It sends no auth, never reads or
 * refreshes the global session, and connects to its app's realtime endpoint
 * anonymously. It is therefore only useful against collections a Bounded app
 * exposes with a public read rule (`read: true`) - private/per-user reads will
 * be denied by the target app's policy, exactly as an anonymous caller would be.
 *
 * Writes and any auth-requiring surface are NOT supported on a scoped instance
 * and throw a clear error. Per-instance authentication is a planned follow-up.
 */
import { get, getMany, search, queryAggregate, count, aggregate, runQuery, runQueryMany, } from './operations';
import { subscribe } from './subscription';
import { DEFAULT_CLIENT_CONFIG, resolveClientConfig } from './config';
/** Error thrown when an unsupported (write / auth-requiring) call is made on a
 *  read-only scoped client. */
export class ScopedClientReadOnlyError extends Error {
    constructor(operation) {
        super(`createClient() instances are read-only in v1: "${operation}" is not supported. ` +
            `Use the global init() client for authenticated writes, or read only public ` +
            `(read: true) collections on this scoped instance.`);
        this.name = 'ScopedClientReadOnlyError';
    }
}
/**
 * Create a read-only client scoped to a single Bounded app.
 *
 * @example
 * ```ts
 * // Global client stays the venue's own authenticated app:
 * await init({ appId: VENUE_APP, network: 'bounded-production', authMethod: 'email' });
 *
 * // A second, read-only client for the steward runtime app:
 * const steward = createClient({ appId: STEWARD_APP, network: 'bounded-production' });
 * const state = await steward.get('runtime/current');
 * const stop = await steward.subscribe('runtime/current', (d) => render(d));
 * ```
 */
export function createClient(config) {
    if (!config || !config.appId) {
        throw new Error('createClient requires an appId.');
    }
    // Resolve from the PRISTINE defaults (never the mutated global) so a scoped
    // instance is fully independent of any prior init(). Same precedence as init:
    // defaults < network preset < explicit fields.
    const resolved = resolveClientConfig(DEFAULT_CLIENT_CONFIG, config);
    // The override channel that carries this instance's identity through the
    // shared transport and subscription code. `_config` retargets endpoints +
    // appId; `_readOnly` omits auth and blocks ambient-session/global-cache reads.
    const instanceOverrides = () => ({ _config: resolved, _readOnly: true });
    // Merge caller-supplied opts with the instance overrides. The instance
    // `_config`/`_readOnly` are applied LAST so app code can never override the
    // routing/identity of a scoped call (but may still pass e.g. an AbortSignal).
    const withOverrides = (opts) => {
        var _a;
        const base = (opts !== null && opts !== void 0 ? opts : {});
        return Object.assign(Object.assign({}, base), { _overrides: Object.assign(Object.assign({}, ((_a = base._overrides) !== null && _a !== void 0 ? _a : {})), instanceOverrides()) });
    };
    return {
        appId: resolved.appId,
        getConfig: () => (Object.assign({}, resolved)),
        get: (path, opts) => get(path, withOverrides(opts)),
        getMany: (paths, opts) => getMany(paths, withOverrides(opts)),
        search: (path, query, opts) => search(path, query, withOverrides(opts)),
        queryAggregate: (path, spec, opts) => queryAggregate(path, spec, withOverrides(opts)),
        count: (path, opts) => count(path, withOverrides(opts)),
        aggregate: (path, operation, opts) => aggregate(path, operation, withOverrides(opts)),
        runQuery: (absolutePath, queryName, queryArgs, opts) => runQuery(absolutePath, queryName, queryArgs, withOverrides(opts)),
        runQueryMany: (many, opts) => runQueryMany(many, withOverrides(opts)),
        subscribe: (path, options) => {
            var _a;
            const opts = typeof options === 'function' ? { onData: options } : Object.assign({}, options);
            // Force this instance's scoped, read-only routing onto the subscription.
            opts._overrides = Object.assign(Object.assign({}, ((_a = opts._overrides) !== null && _a !== void 0 ? _a : {})), { _config: resolved, _readOnly: true });
            return subscribe(path, opts);
        },
        set: () => { throw new ScopedClientReadOnlyError('set'); },
        setMany: () => { throw new ScopedClientReadOnlyError('setMany'); },
    };
}
